import { APIStatus } from '@application/common/enums/api-status';
import { getCurrentUserFromJwt } from '@application/common/helpers/jwt-helper';
import LocalStorageService from '@infrastructure/local-storage-service';
import { createSlice } from '@reduxjs/toolkit';
import { getCurrentUserDetailsAsync } from '../users/userThunks';
import { confirmEmailAsync, createUserAsync, forgotPasswordAsync, getAllEmployeesAsync, getAllUserRolesAsync, getUserByIdAsync, loginAsync, refreshTokenAsync, changePasswordAsync, signUpAsync, updateEmployeeStatusAsync, updateRoleStatusAsync, updateUserAsync, verifyTokenAsync, resetPasswordAsync, getWorkshopLocationAsync, getEmployeesByRoleCategory, getEmployeeSettingsAsync, createEmployeeSettingsAsync } from './authThunks';
import { RoleCategory } from '@application/common/enums/role-category';
const initialState = {
    status: APIStatus.IDLE,
    currentUser: getCurrentUserFromJwt(),
    workShopLocationDropdown: [],
    currentEmployeeSettings: []
};
export const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        logout(state) {
            state.currentUser = undefined;
            LocalStorageService.removeAccessToken();
            LocalStorageService.removeRefreshToken();
            LocalStorageService.removeReferenceNumber();
            LocalStorageService.removeURLHistory();
            state.apiError = undefined;
            state.status = APIStatus.IDLE;
            state.currentUserDetail = undefined;
        },
        resetAuthApiState(state) {
            state.status = APIStatus.IDLE;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(loginAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(loginAsync.fulfilled, (state, action) => {
            state.status = APIStatus.IDLE;
            state.currentUser = getCurrentUserFromJwt(action.payload.accessToken);
            LocalStorageService.setAccessToken(action.payload.accessToken);
            LocalStorageService.setRefreshToken(action.payload.refreshToken);
        })
            .addCase(loginAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(getCurrentUserDetailsAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(getCurrentUserDetailsAsync.fulfilled, (state, action) => {
            state.currentUserDetail = action.payload;
        })
            .addCase(getCurrentUserDetailsAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(refreshTokenAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(refreshTokenAsync.fulfilled, (state, action) => {
            state.status = APIStatus.IDLE;
            state.currentUser = getCurrentUserFromJwt(action.payload.accessToken);
            LocalStorageService.setAccessToken(action.payload.accessToken);
            LocalStorageService.setRefreshToken(action.payload.refreshToken);
        })
            .addCase(refreshTokenAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
        });
        builder
            .addCase(signUpAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(signUpAsync.fulfilled, (state) => {
            state.status = APIStatus.SUCCESS;
        })
            .addCase(signUpAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(confirmEmailAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(confirmEmailAsync.fulfilled, (state) => {
            state.status = APIStatus.SUCCESS;
        })
            .addCase(confirmEmailAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(forgotPasswordAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(forgotPasswordAsync.fulfilled, (state) => {
            state.status = APIStatus.SUCCESS;
        })
            .addCase(forgotPasswordAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(changePasswordAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(changePasswordAsync.fulfilled, (state) => {
            state.status = APIStatus.SUCCESS;
        })
            .addCase(changePasswordAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(verifyTokenAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(verifyTokenAsync.fulfilled, (state) => {
            state.status = APIStatus.SUCCESS;
        })
            .addCase(verifyTokenAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(getAllEmployeesAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(getAllEmployeesAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            state.employee = action.payload;
            state.managerDropdown = action.payload
                .filter((x) => x.categoryId === RoleCategory.Projectleider && x.isActive)
                .map((employee) => {
                return {
                    label: `${employee.firstName} ${employee.lastName} `,
                    value: employee.id,
                    number: `${employee.identifier}`
                };
            });
            state.technicianDropdown = action.payload
                .filter((x) => x.categoryId === RoleCategory.Uitvoerende && x.isActive)
                .map((employee) => {
                return {
                    label: `${employee.firstName} ${employee.lastName} `,
                    value: employee.id,
                    number: `${employee.identifier}`
                };
            });
            state.employeeDropdown = action.payload
                .filter((x) => x.id != null && x.roleId && x.roleId !== 1)
                .map((employee) => {
                return {
                    label: `${employee.identifier} - ${employee.firstName} ${employee.lastName} `,
                    value: employee.id,
                    number: `${employee.identifier}`
                };
            });
        })
            .addCase(getAllEmployeesAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(getUserByIdAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(getUserByIdAsync.fulfilled, (state, action) => {
            state.status = APIStatus.IDLE;
            state.user = action.payload;
        })
            .addCase(getUserByIdAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(getAllUserRolesAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(getAllUserRolesAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            state.roles = action.payload;
            state.userRoleDropdown = action.payload.map((userRole) => {
                return {
                    label: `${userRole.description}`,
                    value: userRole.id
                };
            });
            state.activeUserRoleDropdown = action.payload
                .filter((x) => x.isActive)
                .map((userRole) => {
                return {
                    label: `${userRole.description}`,
                    value: userRole.id
                };
            });
        })
            .addCase(getAllUserRolesAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(createUserAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(createUserAsync.fulfilled, (state) => {
            state.status = APIStatus.IDLE;
        })
            .addCase(createUserAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(updateUserAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(updateUserAsync.fulfilled, (state) => {
            state.status = APIStatus.IDLE;
        })
            .addCase(updateUserAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(updateEmployeeStatusAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(updateEmployeeStatusAsync.fulfilled, (state, action) => {
            state.status = APIStatus.IDLE;
            state.isEmployeeStatusUpdated = action.payload;
        })
            .addCase(updateEmployeeStatusAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(updateRoleStatusAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(updateRoleStatusAsync.fulfilled, (state, action) => {
            state.status = APIStatus.IDLE;
            state.isRoleStatusUpdated = action.payload;
        })
            .addCase(updateRoleStatusAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(resetPasswordAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(resetPasswordAsync.fulfilled, (state) => {
            state.status = APIStatus.SUCCESS;
        })
            .addCase(resetPasswordAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(getWorkshopLocationAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(getWorkshopLocationAsync.fulfilled, (state, action) => {
            state.status = APIStatus.IDLE;
            state.workShopLocation = action.payload;
            state.workShopLocationDropdown = action.payload.map((workshoplocation) => {
                return {
                    label: `${workshoplocation.location}`,
                    value: workshoplocation.id
                };
            });
            state.workShopLocationLabelDropdown = action.payload.map((workshoplocationlabel) => {
                return {
                    label: `${workshoplocationlabel.location}`,
                    value: workshoplocationlabel.location
                };
            });
        })
            .addCase(getWorkshopLocationAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(getEmployeesByRoleCategory.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(getEmployeesByRoleCategory.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            state.activeEmployeesByRoleCategory = action.payload
                .filter((x) => x.isActive)
                .map((employee) => {
                return {
                    label: `${employee.firstName}  ${employee.lastName}`,
                    value: employee.id
                };
            });
            state.employeesByRoleCategory = action.payload.map((employee) => {
                return {
                    label: `${employee.firstName}  ${employee.lastName}`,
                    value: employee.id
                };
            });
        })
            .addCase(getEmployeesByRoleCategory.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(getEmployeeSettingsAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(getEmployeeSettingsAsync.fulfilled, (state, action) => {
            state.status = APIStatus.IDLE;
            state.currentEmployeeSettings = action.payload ?? [];
        })
            .addCase(getEmployeeSettingsAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(createEmployeeSettingsAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(createEmployeeSettingsAsync.fulfilled, (state, action) => {
            state.status = APIStatus.IDLE;
            state.currentEmployeeSettings = action.payload ?? [];
        })
            .addCase(createEmployeeSettingsAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
    }
});
export const { logout, resetAuthApiState } = authSlice.actions;
export default authSlice.reducer;
